// src/components/EndGameInfo/KiddoStars.tsx
import React, { CSSProperties, FC } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { GAMES, GameGroup } from '../../constants/Games';
import { Path } from '../../constants/Path';
import useKiddoStars from '../../hooks/useKiddoStars';
import { getActiveList } from '../../views/auth/slice/selectors';
import classes from './KiddoStars.module.less';

// Gry z kolejki 1–9 — każda ma w rzędzie swoją gwiazdkę, z tym samym numerem co na przycisku w menu.
// Literuj (bez numeru) stoi poza kolejką
const ROW_GAMES = GAMES[GameGroup.KIDDO].filter(({ number }) => number);
// Ostatnia gra kolejki (Napisz) — po niej nie ma już next game
const LAST_GAME = ROW_GAMES[ROW_GAMES.length - 1].path;

// Kształt gwiazdki w polu 24×24 — pucołowata; czubki zaokrągla brzeg (stroke-linejoin: round w stylu)
const STAR_SHAPE = 'M12 2L15.06 8.79L22.46 9.6L16.95 14.61L18.47 21.9L12 18.2L5.53 21.9L7.05 14.61L1.54 9.6L8.94 8.79Z';
const GRADIENT_ID = 'kiddoStarGradient';

// Gradient zdobytych gwiazdek — jeden na ekran, wspólny dla wszystkich; kolory ustala styl (.gradientTop, .gradientBottom)
const StarGradient: FC = () => (
  <svg className={classes.defs} aria-hidden>
    <defs>
      <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" className={classes.gradientTop} />
        <stop offset="1" className={classes.gradientBottom} />
      </linearGradient>
    </defs>
  </svg>
);

// Gwiazdka: puste miejsce (obrys), a na nim zdobyta gwiazdka z odblaskiem i buźką. Kiedy zdobytą widać
// i jak się pojawia, ustala styl (.earned, .new, .bigStar)
const Star: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <path className={classes.slot} d={STAR_SHAPE} />
    <g className={classes.body}>
      <path className={classes.bodyShape} d={STAR_SHAPE} fill={`url(#${GRADIENT_ID})`} />
      <ellipse className={classes.shine} cx="11" cy="6.6" rx="0.65" ry="1.7" transform="rotate(24 11 6.6)" />
      <g className={classes.eyes}>
        <ellipse cx="10" cy="11.7" rx="0.9" ry="1.25" />
        <ellipse cx="14" cy="11.7" rx="0.9" ry="1.25" />
        <circle className={classes.eyeShine} cx="10.3" cy="11.2" r="0.35" />
        <circle className={classes.eyeShine} cx="14.3" cy="11.2" r="0.35" />
      </g>
      <path className={classes.smile} d="M10.3 13.9Q12 15.6 13.7 13.9" />
      <ellipse className={classes.cheek} cx="8.4" cy="13.9" rx="1" ry="0.6" />
      <ellipse className={classes.cheek} cx="15.6" cy="13.9" rx="1" ry="0.6" />
    </g>
  </svg>
);

type StarCellProps = {
  className: string;
  index: number;
  number?: number;
  // Tylko pusta gwiazdka przy podpowiedzi o lukach — wtedy to przycisk, który otwiera jej grę
  onClick?: () => void;
};

// Miejsce w rzędzie: gwiazdka z numerem gry pod spodem
const StarCell: FC<StarCellProps> = ({
  className, index, number, onClick,
}) => {
  const style = { '--index': index } as CSSProperties;
  const content = (
    <>
      <Star />
      <span className={classes.number}>{number}</span>
    </>
  );

  return onClick
    ? <button type="button" className={className} style={style} onClick={onClick}>{content}</button>
    : <div className={className} style={style}>{content}</div>;
};

type StarsRowProps = {
  game: Path;
  // Czy ta gra daje gwiazdkę — nie, gdy dziecko pominęło większość słów
  earnsStar: boolean;
  onRepeat: () => void;
};

const StarsRow: FC<StarsRowProps> = ({ game, earnsStar, onRepeat }) => {
  const { push } = useHistory();
  const earnedBefore = useKiddoStars(game, earnsStar);
  // Gwiazdka tej gry zapala się teraz: gra ukończona pierwszy raz i z gwiazdką
  const isNew = earnsStar && !earnedBefore.includes(game);
  const isEarnedNow = (path: Path) => earnedBefore.includes(path) || (path === game && earnsStar);
  const isRowFull = ROW_GAMES.every(({ path }) => isEarnedNow(path));
  // Rząd zapełnia się właśnie tą grą — tylko wtedy jest świętowanie; potem pełny rząd stoi spokojnie
  const fillsRow = isNew && isRowFull;
  // Podpowiedź przy lukach w rzędzie: duża gwiazda czeka jako pusty obrys, a puste gwiazdki pulsują i otwierają
  // swoje gry. Na końcu Napisz, a potem na końcu każdej gry, gdy Napisz ma już gwiazdkę — dziecko przeszło kolejkę
  // i zostało mu uzupełnianie luk. Dużą gwiazdę dostanie na końcu gry, która uzupełni ostatnią
  const showsGaps = !isRowFull && (game === LAST_GAME || isEarnedNow(LAST_GAME));
  // Pusta gwiazdka tej samej gry zaczyna ją od nowa — przejście pod ten sam adres nie zmieniłoby ekranu
  const openGame = (path: Path) => (path === game ? onRepeat() : push(path));

  return (
    <>
      {fillsRow && <Star className={classNames(classes.bigStar, classes.celebration)} />}
      {showsGaps && <Star className={classNames(classes.bigStar, classes.waiting)} />}

      <div className={classes.row}>
        {ROW_GAMES.map(({ path, number }, index) => {
          const isGap = showsGaps && !isEarnedNow(path);

          return (
            <StarCell
              key={path}
              className={classNames(classes.star, {
                [classes.earned]: earnedBefore.includes(path),
                [classes.new]: isNew && path === game,
                // Gra ukończona już wcześniej podskakuje — ale nie wtedy, gdy dziecko pominęło większość słów
                [classes.again]: earnsStar && !isNew && path === game,
                [classes.wave]: fillsRow,
                [classes.gap]: isGap,
              })}
              index={index}
              number={number}
              onClick={isGap ? () => openGame(path) : undefined}
            />
          );
        })}
      </div>
    </>
  );
};

type KiddoStarsProps = {
  // Ile słów listy dziecko zrobiło — podają tylko gry ze SKIP (Ułóż słowa, Napisz, Literuj)
  wordsDone?: number;
  // Restart tej gry — jak repeat game
  onRepeat: () => void;
};

// Zakończenie gier Easy zamiast żarówki i napisu. Gry 1–9 pokazują rząd gwiazdek i zapalają w nim swoją;
// Literuj — jedną dużą gwiazdę, bez rzędu i bez zapisu (pustą, gdy dziecko pominęło większość słów)
const KiddoStars: FC<KiddoStarsProps> = ({ wordsDone, onRepeat }) => {
  const { pathname } = useLocation();
  const wordsCount = useSelector(getActiveList)?.words?.length ?? 0;
  const game = ROW_GAMES.find(({ path }) => path === pathname.replace(/\/$/, ''));
  // Gwiazdka za grę, w której dziecko zrobiło co najmniej połowę słów listy, czyli pominęło (SKIP) najwyżej połowę.
  // Gry bez SKIP liczby słów nie podają i gwiazdkę dają zawsze
  const earnsStar = wordsDone === undefined || wordsDone * 2 >= wordsCount;

  return (
    <div className={classes.stars}>
      <StarGradient />
      {game
        ? <StarsRow game={game.path} earnsStar={earnsStar} onRepeat={onRepeat} />
        : <Star className={classNames(classes.bigStar, { [classes.waiting]: !earnsStar })} />}
    </div>
  );
};

export default KiddoStars;
